import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { FcGoogle } from "react-icons/fc";
import { FaEnvelope, FaMobileAlt, FaLock, FaEye, FaEyeSlash } from "react-icons/fa";

import { toast } from "react-hot-toast";
import { motion } from "framer-motion";
import { auth, googleProvider } from "../firebase";
import { signInWithPopup } from "firebase/auth";
import api from "../utils/base_url";
import { useAuth } from "../context/AuthContext";

const Register = () => {
  const [registerMethod, setRegisterMethod] = useState("phone");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [phone, setPhone] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [errors, setErrors] = useState("");
  const { login } = useAuth();

  const navigate = useNavigate();

  // Validate email format
  const isValidEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  // Validate password (min 6 characters)
  const isValidPassword = (password) =>
    /^(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]).{6,}$/.test(password);
  // Validate phone (10 digits)
  const isValidPhone = (phone) => /^[0-9]{10,15}$/.test(phone);
  const handleEmailRegister = async (e) => {
    e.preventDefault();

    // basic client validations
    if (!isValidEmail(email)) {
      setErrors("Please enter a valid email");
      setTimeout(() => setErrors(""), 3000);
      return;
    }

    if (!isValidPassword(password)) {
      setErrors(
        "Password must have at least 1 uppercase letter, 1 number, 1 special character, and minimum 6 characters"
      );
      setTimeout(() => setErrors(""), 3000);
      return;
    }

    setLoading(true);
    try {
      const res = await api.post("register/email-step1/", { email, password });

      toast.success(res.data.message || "OTP sent to email");

      sessionStorage.setItem("session_id", res.data.session_id);
      sessionStorage.setItem("email", email);
      sessionStorage.setItem("password", password);

      navigate("/verification", { state: { type: "email" } });

    } catch (error) {
      const data = error.response?.data;

      // 🔥 universal backend error extractor
      const errorMessage =
        data?.error ||
        data?.message ||
        (typeof data === "object"
          ? Object.values(data)?.[0]?.[0] || Object.values(data)?.[0]
          : null) ||
        "Email registration failed";

      setErrors(errorMessage);

      setTimeout(() => setErrors(""), 3000);

    } finally {
      setLoading(false);
    }
  };


const handlePhoneRegister = async (e) => {
    e.preventDefault();
    if (!isValidPhone(phone)) return toast.error("Please enter a valid phone number");

    setLoading(true);
    try {
      const res = await api.post("phone-register-step1/", { phone });
      toast.success(res.data.message || "OTP sent to phone");
      sessionStorage.setItem("session_id", res.data.session_id);
      sessionStorage.setItem("phone", phone);
      navigate("/verification", { state: { type: "phone" } });
    } catch (error) {
      const backendError = error.response?.data;
      const errorMessage = extractErrorMessage(backendError);
      setErrors(errorMessage);
      setTimeout(() => {
        setErrors("");
      }, 3000);
    } finally {
      setLoading(false);
    }
  };

  // Put this outside the component (or in a shared utils file) so it can be reused
  // across PhoneLoginStep1, PhoneRegisterStep1, etc.
  const extractErrorMessage = (backendError) => {
    if (!backendError || typeof backendError !== "object") {
      return "Something went wrong. Please try again.";
    }

    // 1. Meta WhatsApp API failure: { message: "...", error: { error: { message: "..." } } }
    const metaMessage = backendError.error?.error?.message;
    if (metaMessage) return metaMessage;

    // 2. Serializer validation errors: { errors: { phone: ["Already taken"] } }
    if (backendError.errors && typeof backendError.errors === "object") {
      const firstKey = Object.keys(backendError.errors)[0];
      const firstError = backendError.errors[firstKey];
      if (Array.isArray(firstError)) return firstError[0];
      if (typeof firstError === "string") return firstError;
    }

    // 3. Generic top-level message: { message: "WhatsApp OTP failed" }
    if (typeof backendError.message === "string") return backendError.message;

    // 4. Fallback: old-style flat errors { phone: ["Already taken"] } with no wrapper
    const excludeKeys = ["status", "message", "error", "errors"];
    const fallbackKey = Object.keys(backendError).find((k) => !excludeKeys.includes(k));
    if (fallbackKey) {
      const val = backendError[fallbackKey];
      if (Array.isArray(val)) return val[0];
      if (typeof val === "string") return val;
    }

    return "Phone registration failed";
  };

  const handleGoogleAuth = async () => {
    setLoading(true);
    try {
      // 🔥 Google popup
      const result = await signInWithPopup(auth, googleProvider);

      // 🔥 Firebase ID token
      const idToken = await result.user.getIdToken();

      // 🔥 Call your backend (single endpoint)
      const res = await api.post("google/", { token: idToken });

      // 🔥 Store tokens
      sessionStorage.setItem("access", res.data.access);
      sessionStorage.setItem("refresh", res.data.refresh);

      // 🔥 Login user in AuthContext
      login({
        access: res.data.access,
        refresh: res.data.refresh,
        user: res.data.user,
      });

      toast.success("Google login successful!");
      navigate("/");

    } catch (error) {
      const msg =
        error.response?.data?.error ||
        error.response?.data?.message ||
        error.message ||
        "Google login failed";

      setErrors(msg);
      setTimeout(() => {
        setErrors("");
      }, 3000);
    } finally {
      setLoading(false);
    }
  };


  return (
    <div className="register-container">
      <motion.div
        initial={{ y: 50, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.8 }}
        className="register-card"
      >
        <h4 className="register-title">Create Your Account</h4>

        <img
          src={`${process.env.PUBLIC_URL}/Logo.png`}
          alt="Logo"
          className="navbar-logo"
        />

        <div className="register-method-toggle">
           <motion.button
            className={registerMethod === "phone" ? "active" : ""}
            onClick={() => setRegisterMethod("phone")}
            whileTap={{ scale: 0.95 }}
            aria-label="Phone Register"
          >
            <FaMobileAlt size={24} />
          </motion.button> 
          <motion.button
            className={registerMethod === "email" ? "active" : ""}
            onClick={() => setRegisterMethod("email")}
            whileTap={{ scale: 0.95 }}
            aria-label="Email Register"
          >
            <FaEnvelope size={24} />
          </motion.button>
        </div>

        {registerMethod === "email" && (
          <motion.form onSubmit={handleEmailRegister} className="register-form">
            <div className="floating-input">
              <input
                type="email"
                placeholder=" "
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
              <label>
                <FaEnvelope /> Email
              </label>
            </div>

            <div className="floating-input password-input">
              <input
                type={showPassword ? "text" : "password"}
                placeholder=" "
                value={password}
                required
                onChange={(e) => setPassword(e.target.value)}
              />
              <label>
                <FaLock /> Create Password
              </label>
              <button
                type="button"
                className="password-toggle"
                onClick={() => setShowPassword(!showPassword)}
              >
                {showPassword ? <FaEyeSlash /> : <FaEye />}
              </button>
            </div>
            {typeof errors === "string" && errors.trim() !== "" && (
              <p style={{ color: "red", textAlign: "center", marginTop: "-10px" }}>
                {errors}
              </p>
            )}


            <button type="submit" className="btn btn-primary register-btn" disabled={loading}>
              {loading ? "Please wait..." : "Register"}
            </button>
          </motion.form>
        )}

        {registerMethod === "phone" && (
          <motion.form onSubmit={handlePhoneRegister} className="register-form">
            <div className="floating-input">
              <input
                type="tel"
                placeholder=" "
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                required
              />
              <label>
                <FaMobileAlt /> Phone Number
              </label>
            </div>
            {typeof errors === "string" && errors.trim() !== "" && (
              <p style={{ color: "red", textAlign: "center", marginTop: "-10px" }}>
                {errors}
              </p>
            )}

            <button type="submit" className="btn btn-primary register-btn" disabled={loading}>
              {loading ? "Sending..." : "Send OTP"}
            </button>
          </motion.form>
        )}

        <button
          onClick={handleGoogleAuth}
          className="btn btn-google-register"
          disabled={loading}
          aria-label="Register with Google"
          style={{ border: "1px solid #8A4B27" }}
        >
          <FcGoogle size={24} />
          Login with Google
        </button>

        <p className="login-prompt">
          Already have an account?{" "}
          <Link to="/login" className="text-decoration-underline text-themed">
            Login
          </Link>
        </p>
      </motion.div>

      <style>{`

            .navbar-logo {
  height: 50px;       /* reduce from 100px (too big for navbar) */
  width: auto;       /* keep clarity */
  display: block;
  margin-bottom: 12px;
  
}

        .register-container {
          display: flex;
          justify-content: center;
          align-items: center;
          min-height: 100vh;
          background-color: #E9DCC3;
          padding: 20px;
        }
        .register-card {
          background: #E9DCC3;
          border: 1.5px solid #4A1F1A;
          border-radius: 20px;
          box-shadow: 0 6px 18px #4A1F1A;
          padding: 50px 35px;
          width: 100%;
          max-width: 440px;
          color: #4A1F1A;
          font-weight: 600;
          text-align: center;
          font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
        }
        .register-title {
          font-size: 2rem;
          font-weight: 700;
          margin-bottom: 12px;
          color: #000000ff;
        }
        .register-method-toggle {
          display: flex;
          justify-content: center;
          gap: 15px;
          margin-bottom: 30px;
          padding: 10px;
          background: #ffffffff;
          border: 1.5px solid #4A1F1A;
          border-radius: 14px;
        }

        .register-method-toggle button {
          width: 64px;
          height: 50px;
          border-radius: 12px; /* 🔲 BOX */
          border: 2px solid #4A1F1A;
          background: #E9DCC3;
          color: #4A1F1A;
          cursor: pointer;
          font-size: 22px;
          font-weight: 700;
          display: flex;
          justify-content: center;
          align-items: center;
          transition: all 0.3s ease;
        }

        .register-method-toggle button:hover {
          background: #e8f8e8;
        }

        .register-method-toggle button.active {
          background: #4A1F1A;
          color: #E9DCC3;
          box-shadow: 0 6px 15px #4A1F1A;
        }

        .register-form {
          display: flex;
          flex-direction: column;
          gap: 22px;
          text-align: left;
          
        }
        .floating-input {
          position: relative;
          
        }
        .floating-input input {
          width: 100%;
          border-radius: 15px;
          border: 1.5px solid #f1e6d4;
          background-color: #f9fff9;
          font-weight: 600;
          font-size: 1.1rem;
          color: #000000ff;
          padding: 14px 14px 14px 42px;
          box-shadow: inset 0 0 6px rgba(245, 245, 245, 0.5);
          outline: none;
          transition: border-color 0.3s ease, box-shadow 0.3s ease;
        }
        .floating-input input:focus {
          border-color: #2E7D32;
          box-shadow: 0 0 10px rgba(122, 86, 58, 0.4);
        }
        .floating-input input::placeholder {
          color: transparent;
        }
        .floating-input label {
          position: absolute;
          left: 42px;
          top: 50%;
          transform: translateY(-50%);
          pointer-events: none;
          font-weight: 600;
          color: #000000ff;
          font-size: 1rem;
          display: flex;
          align-items: center;
          gap: 8px;
          transition: all 0.3s ease;
          user-select: none;
        }
        .floating-input input:focus + label,
        .floating-input input:not(:placeholder-shown) + label {
          top: 5px;
          font-size: 0.85rem;
          color: #4A1F1A;
          font-weight: 700;
          background: #fffaf4;
          padding: 0 8px;
          border-radius: 8px;
          left: 15px;
        }
        .btn-primary.register-btn {
          background-color: #4A1F1A;
          color: #fff;
          border-radius: 8px;
          padding: 15px 0;
          font-weight: 700;
          font-size: 1.2rem;
          border: none;
          cursor: pointer;
          box-shadow: 0 6px 18px #8A4B27 ;
          transition: background-color 0.3s ease;
        }
        .btn-primary.register-btn:hover,
        .btn-primary.register-btn:focus {
          background-color: #8A4B27;
          outline: none;
          box-shadow: 0 8px 24px #4A1F1A;
        }
      .btn-google-register {
margin-top: 35px;
          background: #000000ff important;
          border: none;
          border-radius: 14px; /* 🔲 BOX */
          padding: 14px 0;
          font-weight: 700;
          font-size: 1.15rem;
          color: #000000;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 14px;
          width: 100%;
          box-shadow: 0 6px 18px #8A4B27;
          transition: all 0.3s ease;
        }

        .btn-google-register:hover:not(:disabled) {
          transform: translateY(-2px);
          box-shadow: 0 10px 25px #4A1F1A;
        }

        .btn-google-register:disabled {
          opacity: 0.7;
          cursor: not-allowed;
        }

        .google-icon {
          margin-left: -6px;
        }
        .login-prompt {
          margin-top: 38px;
          text-align: center;
          font-weight: 600;
          color: #000000ff;
          font-size: 1rem;
        }
        .login-prompt a {
          color: #4A1F1A;
          font-weight: 700;
          text-decoration: underline;
          transition: color 0.3s ease;
        }
        .login-prompt a:hover {
          color: #8A4B27;
        }
        .password-input { position: relative; }
          .password-toggle {
          position: absolute;
          right: 14px;
          top: 50%;
          transform: translateY(-50%);
          border: none;
          background: transparent;
          color: #000000ff;
          cursor: pointer;
          font-size: 1.25rem;
        }
        @media (max-width: 768px) {
          .register-card{
            padding: 40px 25px;
            max-width: 100%;
          }
          .register-title {
            font-size: 1.6rem;
          }
          .btn-primary.register-btn,
          .btn-google-register {
            font-size: 1rem;
            padding: 12px 0;
          }
          .register-method-toggle button {
            width: 50px;
            height: 50px;
            font-size: 22px;
          }
        }
      `}</style>
    </div>
  );
};

export default Register;
